/**
 * The job record v2 (BENCH-V2-SPEC §12.1) and the pure rules around it: building a record, merging a write into the stored
 * record (`createdAt` is kept, spec §15 item 12), the v1 migration, and the resume check (§12.3). No storage here, so the rules
 * are tested in Node; `store.ts` holds the IndexedDB calls.
 *
 * The record keeps the run's `plan` (cuts, units, re-cuts), `edits`, `unitStates`, `ledger`, `floors` and the rest as one
 * `snapshotJson`, the JSON string of a `RunSnapshot` that the bench worker serialises (ED-59): the form the run restores from. `batches` and the Economy phase are the job's own.
 */
import type { DocumentBrief } from '../../engine/bench/brief.ts';
import type { KeepTier } from '../../engine/bench/keep.ts';
import type { ProviderAnswer, RunReport, Usage } from '../../engine/bench/loop2.ts';
import type { ChannelId } from '../../engine/channels/types.ts';
import type { AnalysisSummary } from '../../shared/summary.ts';
import type { SourceKind } from '../../shared/viewmodel.ts';
import type { RewriteScope } from './state.ts';

export const JOB_SCHEMA = 3;

export type JobStatus = 'running' | 'paused' | 'done' | 'cancelled' | 'error';

/** The page's state as `run-watch.ts` records it while a Standard job runs. */
export type SeenState = 'visible' | 'hidden' | 'pagehide' | 'offline';

/** What the running page last wrote about itself: its state, since when, and when it was last alive (ISO times). */
export interface Seen {
	state: SeenState;
	since: string;
	at: string;
}

/**
 * Why a run stopped (DESIGN UD-38), when it can be known: `closed` (the page was closed or reloaded), `hidden` (it was in the
 * background and then stopped), `offline` (the network went away), `lost` (the page stopped and nothing says how: closed,
 * reloaded, or the computer slept), `budget` (the spending limit) or `error` (the cause is in the job's `error`). `at` is when
 * it happened; `lastSeenAt` is when the page was last alive (`hidden` and `lost`).
 */
export type StopReason = 'closed' | 'hidden' | 'offline' | 'lost' | 'budget' | 'error';
export interface StopInfo {
	reason: StopReason;
	at?: string;
	lastSeenAt?: string;
}

/** One submitted batch of an Economy job: the round it belongs to and what it holds. */
export interface BatchRecord {
	/** 0 for the controls, then 1, 2, 3 for the rewrite rounds. */
	round: number;
	/** One batch, or several when the round was split to fit the relay's body cap. */
	batchIds: string[];
	/** Request key (chunk key, or `control:<n>`) by custom id: how a collected result finds its chunk. */
	keys: Record<string, string>;
	/** Set once its results were applied to the run. */
	applied?: boolean;
}

export interface BenchJob {
	id: string;
	schema: typeof JOB_SCHEMA;
	docHash: string;
	scope: RewriteScope;
	mode: string;
	only?: ChannelId[];
	economy: boolean;
	model: string;
	effort: string;
	/**
	 * Standard: requests at once (DESIGN ED-86); the run uses the parallel scheduler. Absent on Economy jobs and on jobs
	 * from before v3, which resume on the sequential path their snapshot was taken on.
	 */
	concurrency?: number;
	/**
	 * The document brief the job's requests carry (BENCH-V3-PLAN §8): made once, before the first request, and kept here so a
	 * resume sends the same notes. Absent on jobs from before v3 and when the brief could not be made.
	 */
	brief?: DocumentBrief;
	/**
	 * Versions per passage the job's rewrite requests ask for (BENCH-V3-PLAN §6, ED-90), 2 or 3; absent is one (jobs from before
	 * schema 3, and the setting at 1). Kept so a resume asks as the run did.
	 */
	versions?: number;
	/**
	 * The keep standard of the job (BENCH-V3-PLAN §7.1, ED-92), `lenient`; absent is `strict` (jobs from before it, and the
	 * default). Kept so a resume ends the run as it was started.
	 */
	keep?: KeepTier;
	/**
	 * The editorial guardrails of the job (DESIGN ED-191, UD-41), `true` when it was started with them; absent is off (jobs from
	 * before them, and the default). Kept so a resume asks as the run did; older builds ignore the field.
	 */
	guardrails?: true;
	/**
	 * Who the job is about (UD-21 jobs list): the analysed document's name (`meta.name`), how it came in, and its word count,
	 * taken when the job is created. Absent on jobs from before they were kept; the list then shows "Unnamed document" and the
	 * first 8 characters of `docHash`.
	 */
	docName?: string;
	sourceKind?: SourceKind;
	words?: number;
	/**
	 * The before and after of the overview (UD-22): the analysis summaries of the working text the run started from
	 * (`baseText`) and of the rewritten text (`resultText`), both through the same plain-text pipeline, made the first time the
	 * finished job is opened. The original file's own analysis is not used as "before": it reads the file's formatting, so an
	 * unchanged text would show changes of several points (on the sample, PROC-03 83 against 77). Absent on jobs from before
	 * them; the overview then makes what is missing and stores it.
	 */
	before?: AnalysisSummary;
	after?: AnalysisSummary;
	/**
	 * The version of its document the job started from (DESIGN UD-27, `chain.ts`): its number and the hash of its text.
	 * `docHash` is the document's (version 0's) hash. Absent on jobs from before the version chain: they started from the
	 * upload, version 0.
	 */
	base?: { n: number; hash: string };
	createdAt: string;
	updatedAt: string;
	status: JobStatus;
	/** Why the run stopped, when known (UD-38); cleared when the job runs again. */
	stop?: StopInfo;
	doneChunks: number;
	totalChunks: number;
	/**
	 * The run at its last chunk (Standard) or round (Economy) boundary, as the JSON string the worker serialised (ED-59, spec
	 * §12.1): the main thread stores it and hands it back to a worker on a resume, and never parses it. Dropped when the job is done.
	 */
	snapshotJson?: string;
	coverage?: RunReport['coverage'];
	batches: BatchRecord[];
	/** Economy: the stage the job is in (`controls`, then `rounds`) and the rounds applied so far. */
	phase?: 'controls' | 'rounds';
	round?: number;
	/** Economy: answers collected for the round in hand, not yet applied (a reload between collect and apply). */
	collected?: Array<[string, ProviderAnswer]>;
	usage: Usage;
	costUsd: number;
	/** The budget guard (SECURITY §3): carried across a pause, raised once per continue (spec §15 item 5). */
	spentUsd: number;
	limitUsd: number;
	/**
	 * The working text the edits are relative to (a fixed point of the plain-text analysis): kept when the job is created (and
	 * on the resume of a job from before that), so the overview's "before" is measured on the text the run rewrote (UD-22).
	 */
	baseText?: string;
	/** The finished run, for review. */
	report?: RunReport;
	resultText?: string;
	error?: string;
}

export const emptyUsage = (): Usage => ({
	requests: 0,
	inputTokens: 0,
	outputTokens: 0,
	cacheReadTokens: 0,
	cacheWriteTokens: 0,
});

export function newJob(init: {
	id: string;
	docHash: string;
	scope: RewriteScope;
	mode: string;
	only?: readonly ChannelId[];
	economy: boolean;
	model: string;
	effort: string;
	concurrency?: number;
	versions?: number;
	keep?: KeepTier;
	/** The editorial guardrails (ED-191): stored only when on. */
	guardrails?: boolean;
	docName?: string;
	sourceKind?: SourceKind;
	words?: number;
	before?: AnalysisSummary;
	baseText?: string;
	base?: { n: number; hash: string };
	totalChunks: number;
	limitUsd: number;
	now?: string;
}): BenchJob {
	const now = init.now ?? new Date().toISOString();
	return {
		id: init.id,
		schema: JOB_SCHEMA,
		docHash: init.docHash,
		scope: init.scope,
		mode: init.mode,
		...(init.only ? { only: [...init.only] } : {}),
		economy: init.economy,
		model: init.model,
		effort: init.effort,
		...(init.concurrency !== undefined ? { concurrency: init.concurrency } : {}),
		...(init.versions !== undefined && init.versions > 1 ? { versions: init.versions } : {}),
		...(init.keep === 'lenient' ? { keep: init.keep } : {}),
		...(init.guardrails ? { guardrails: true as const } : {}),
		...(init.docName !== undefined ? { docName: init.docName } : {}),
		...(init.sourceKind !== undefined ? { sourceKind: init.sourceKind } : {}),
		...(init.words !== undefined ? { words: init.words } : {}),
		...(init.before !== undefined ? { before: init.before } : {}),
		...(init.baseText !== undefined ? { baseText: init.baseText } : {}),
		...(init.base !== undefined ? { base: { ...init.base } } : {}),
		createdAt: now,
		updatedAt: now,
		status: 'running',
		doneChunks: 0,
		totalChunks: init.totalChunks,
		batches: [],
		usage: emptyUsage(),
		costUsd: 0,
		spentUsd: 0,
		limitUsd: init.limitUsd,
	};
}

/** A write goes over the stored record, which keeps its `createdAt` (spec §15 item 12: it was overwritten on every put). */
export function mergeJob(existing: BenchJob | undefined, next: BenchJob): BenchJob {
	return { ...next, createdAt: existing?.createdAt ?? next.createdAt };
}

/** The shape a v1 record had (BENCH-UX-SPEC §3). */
interface JobV1 {
	id: string;
	scope: RewriteScope;
	mode: string;
	economy: boolean;
	docHash: string;
	startedAt: string;
	updatedAt: string;
	status: JobStatus;
	doneChunks: number;
	totalChunks: number;
	resultText?: string;
	error?: string;
}

/**
 * A v1 record becomes a v2 record without its resume fields (`outcomes`, and the Economy batch that was never stored): the
 * identity fields, the progress and the result text stay. A v1 job still `running` was never resumable, so it is `error`
 * with the reason.
 */
export function migrateJob(v1: JobV1 | BenchJob): BenchJob {
	const schema = (v1 as { schema?: number }).schema;
	if (schema === JOB_SCHEMA || schema === 2) {
		// a record written before ED-59 holds the snapshot as an object: it becomes the string (once, on load)
		const { snapshot, ...rest } = v1 as BenchJob & { snapshot?: unknown };
		// schema 2 (versions 2.4 and 2.5) becomes schema 3 with nothing else to change: `versions` absent means one version
		// per passage, which is what a schema 2 run asked for
		if (snapshot === undefined && schema === JOB_SCHEMA) return v1 as BenchJob;
		return {
			...rest,
			schema: JOB_SCHEMA,
			...(snapshot !== undefined && !rest.snapshotJson
				? { snapshotJson: JSON.stringify(snapshot) }
				: {}),
		};
	}
	const old = v1 as JobV1;
	const interrupted = old.status === 'running' || old.status === 'paused';
	return {
		id: old.id,
		schema: JOB_SCHEMA,
		docHash: old.docHash,
		scope: old.scope,
		mode: old.mode,
		economy: old.economy,
		model: '',
		effort: '',
		createdAt: old.startedAt,
		updatedAt: old.updatedAt,
		status: interrupted ? 'error' : old.status,
		doneChunks: old.doneChunks,
		totalChunks: old.totalChunks,
		batches: [],
		usage: emptyUsage(),
		costUsd: 0,
		spentUsd: 0,
		limitUsd: 0,
		...(old.resultText !== undefined ? { resultText: old.resultText } : {}),
		...(old.error !== undefined
			? { error: old.error }
			: interrupted
				? { error: 'an earlier version of the app started this job and cannot resume it' }
				: {}),
	};
}

export type ResumeCheck = { ok: true } | { ok: false; reason: string };

/** §12.3: a job resumes only against the document it was run on, with the state the mode needs. */
export function canResume(job: BenchJob, docHash: string | undefined): ResumeCheck {
	if (job.schema !== JOB_SCHEMA)
		return { ok: false, reason: 'this job was made by an earlier version of the app' };
	if (docHash === undefined || job.docHash !== docHash)
		return { ok: false, reason: 'the open document is not the one this job was run on' };
	if (job.status === 'done' || job.status === 'cancelled')
		return { ok: false, reason: `the job is ${job.status}` };
	if (!job.snapshotJson) return { ok: false, reason: 'the job holds no saved run state' };
	if (job.economy && job.batches.length === 0 && !job.collected)
		return { ok: false, reason: 'the job holds no submitted batch' };
	return { ok: true };
}

/** What a lost page's last record says about how the run stopped (UD-38). */
export function stopFromSeen(seen: Seen | null): StopInfo {
	if (!seen) return { reason: 'lost' };
	if (seen.state === 'pagehide') return { reason: 'closed', at: seen.since };
	if (seen.state === 'offline') return { reason: 'offline', at: seen.since };
	if (seen.state === 'hidden') return { reason: 'hidden', at: seen.since, lastSeenAt: seen.at };
	return { reason: 'lost', lastSeenAt: seen.at };
}

/**
 * True when a Standard job stored as `running` is being run by another page: its run lock is held (UD-38). `held` is the set
 * of held job ids, or null where the Web Locks API is missing (then no job can be known to run elsewhere).
 */
export const runsElsewhere = (
	job: BenchJob,
	held: ReadonlySet<string> | null | undefined
): boolean => job.status === 'running' && !job.economy && !!held && held.has(job.id);

/**
 * What the job list shows after a reload: a Standard job still `running` whose lock nobody holds lost its runner, so it is
 * `paused` (resumable from its last chunk) with the reason `seen` gives; one whose lock is held runs in another tab and stays
 * `running`. Without lock information (`held` null or absent) every such job is paused, as before the locks. An Economy job
 * keeps running, since its batches continue on the server.
 */
export function reconcileJob(
	job: BenchJob,
	held?: ReadonlySet<string> | null,
	seen?: Seen | null
): BenchJob {
	if (job.status === 'running' && !job.economy) {
		if (runsElsewhere(job, held)) return job;
		return { ...job, status: 'paused', stop: stopFromSeen(seen ?? null) };
	}
	return job;
}
